/* eslint-disable @typescript-eslint/no-explicit-any */
import { ApolloCache } from "@apollo/client";
import { FetchManualContactsView, UpdateInterestFormData, WaitlistUpdateQuery,
  FetchManualContactsView_interestList_scheduledUpdate_manualContacts_results_phones as PhonesType } from "types/gql-types";
import { WAITLIST_UPDATE_QUERY } from "pages/waitlist-update/dashboard/query";
import { QUERY_MANUAL_CONTACTS } from "pages/waitlist-update/manual-contact/query";
import { QUERY_INTEREST_PROFILE } from "../interests-profile/query";
import QUERY_INTEREST_DATA from "./query-interest-data.gql";

type QueryLog = {
  query: string | undefined;
  variables: Record<string, any>;
  operationName: string;
}
const addressesFromInterest = (interest: any): PhonesType[] =>
  [
    {
      label: "Primary",
      number: interest.householdMember0PrimaryPhone,
      type: interest.householdMember0PrimaryPhoneType || "mobile",
      enabled: interest.contactMethodPhone,
      ext: null,
    },
    {
      label: "Secondary",
      number: interest.householdMember0SecondaryPhone,
      type: interest.householdMember0SecondaryPhoneType || "mobile",
      ext: interest.householdMember0SecondaryPhoneExt,
      enabled: null,
    },
    {
      label: "Optional Contact",
      number: interest.contactPrimaryPhone,
      type: interest.contactPrimaryPhoneType || "mobile",
      ext: interest.contactPrimaryPhoneExt,
      enabled: null,
    },
    {
      label: "Optional Contact Additional",
      number: interest.contactSecondaryPhone,
      type: interest.contactSecondaryPhoneType || "mobile",
      ext: interest.contactSecondaryPhoneExt,
      enabled: null,
    },
  ].map((address) => ({ ...address, __typename: "LabelledPhone" as const }));

const mergePhones = (array1: PhonesType[], array2: PhonesType[]): PhonesType[] => {
  const replacedArray = array1.map(obj1 => {
    const match = array2.find(obj2 => obj2.label === obj1.label);
    return match ?? obj1;
  });

  const newItems = array2.filter(obj2 => !array1.some(obj1 => obj1.label === obj2.label));

  return replacedArray.concat(newItems);
};

const isObject = (val: any) => typeof val === "object" && val !== null && !Array.isArray(val);

const updateObject =
<T extends Record<string, any>>(target: T, updates: Partial<Record<keyof T, any>>): T =>
  Object.entries(target).reduce((acc, [key, value]) => {
    const updateValue = updates[key as keyof T];

    if (key in updates) {
      const shouldMergeObjects = isObject(value) && isObject(updateValue);
      acc[key as keyof T] = shouldMergeObjects ? (
        updateObject(value, updateValue)) : updateValue ?? value;
    } else {
      acc[key as keyof T] = value;
    }

    return acc;
  }, { ...target });


/**
 * Returns the best available name for the given interest, or an empty string if
 * no name exists.
 * @param {Object} interest - The interest object.
 * @returns {string} The best available name or an empty string.
 */
const getBestName = (interest: any) => {

  // Extract first and last names from the interest object.
  const firstName = interest.householdMember0FirstName || "";
  const lastName = interest.householdMember0LastName || "";

  // Construct full name if both first and last names exist.
  const fullName = firstName && lastName ? `${firstName} ${lastName}` : "";

  // Return the best available name.
  if (fullName) {
    return fullName;
  } else if (firstName) {
    return firstName;
  } else if (lastName) {
    return lastName;
  }
  return "";

};

// Function to handle waitlist updates specifically
const updateWaitlistResults = (
  current: WaitlistUpdateQuery,
  interest: any,
  interestID: string,
) => {
  const { results } = current.interestList.scheduledUpdate.requested;
  const shouldUpdate = results.some(result => result.interestId === interestID);

  if (!shouldUpdate) return null;

  const updatedResults = results.map(result => {
    if (result.interestId !== interestID) return result;

    const updatedData = { ...result };
    const fieldsToUpdate = ["householdMember0SecondaryPhone", "householdMember0PrimaryPhone"];
    fieldsToUpdate.forEach(field => {
      if (interest?.[field]) {
        updatedData.phone = interest[field];
      }
    });
    const bestName = getBestName(interest);
    if (bestName) updatedData.name = bestName;

    return updatedData;
  });

  return {
    ...current,
    interestList: {
      ...current.interestList,
      scheduledUpdate: {
        ...current.interestList.scheduledUpdate,
        requested: {
          ...current.interestList.scheduledUpdate.requested,
          results: updatedResults,
        },
      },
    },
  };
};

const updateManualContactResults = (
  current: FetchManualContactsView,
  interest: any,
  interestID: string,
) => {
  const { results } = current.interestList.scheduledUpdate.manualContacts;
  const shouldUpdate = results.some(result => result.interestId === interestID);

  if (!shouldUpdate) return null;

  const updatedResults = results.map(result => {
    if (result.interestId !== interestID) return result;

    const updatedData = { ...result };


    const newPhones = addressesFromInterest(interest);
    const mergedPhones = mergePhones(updatedData.phones, newPhones)
      .filter(phone => phone.number);
    const bestName = getBestName(interest);
    if (bestName) updatedData.name = bestName;
    updatedData.phones = mergedPhones;

    return updatedData;
  });

  return {
    ...current,
    interestList: {
      ...current.interestList,
      scheduledUpdate: {
        ...current.interestList.scheduledUpdate,
        manualContacts: {
          ...current.interestList.scheduledUpdate.manualContacts,
          results: updatedResults,
        },
      },
    },
  };
};

type CacheVariables = {
  interestId: string;
  interestListId: string;
}

export const updateCachedQueries = (
  cache: ApolloCache<any>,
  data: UpdateInterestFormData,
  variables: CacheVariables,
): void => {
  const { interest } = data.pmUpdateCompletedInterest;
  const updateCacheEntry = (
    query: any,
    entryVariables: any,
    updateFn: (data: any, newData: any) => any,
  ) => {
    const currentData = cache.readQuery({ query, variables: entryVariables });
    if (currentData) {
      const updatedData = updateFn(currentData, data);
      if (updatedData) {
        cache.writeQuery({
          query,
          data: updatedData,
          variables: entryVariables,
        });
      }
    }
  };
  updateCacheEntry(QUERY_INTEREST_DATA, variables, (currentData) => ({
    ...currentData,
    interest: updateObject(currentData.interest, interest),
  }));
  updateCacheEntry(QUERY_INTEREST_PROFILE, { interestId: variables.interestId }, (currentData) => ({
    ...currentData,
    interest: updateObject(currentData.interest, interest),
  }));

  const storedQueryLogs: QueryLog[] = JSON.parse(sessionStorage.getItem("apolloQueryLogs") ?? "[]");
  storedQueryLogs.filter(log => log.operationName === "WaitlistUpdateQuery").forEach(({ variables: queryVariables }) => {
    updateCacheEntry(WAITLIST_UPDATE_QUERY, queryVariables, (currentData) => {
      const updatedData =
        updateWaitlistResults(currentData, interest, variables.interestId);
      return updatedData ?? currentData;
    });
  });
  storedQueryLogs.filter(log => log.operationName === "FetchManualContactsView").forEach(({ variables: queryVariables }) => {
    updateCacheEntry(QUERY_MANUAL_CONTACTS, queryVariables, (currentData) => {
      const updatedData =
        updateManualContactResults(currentData, interest, variables.interestId);
      return updatedData ?? currentData;
    });
  });
};
